import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { DataService } from './data.service';

export interface TwilioMessage {
  id?: string;
  referralId?: string;
  patientId: string;
  body: string;
  senderName: string;
  firstName?: string;
  lastName?: string;
  userId?: string;
  callType?: string;
  messageType?: string;
  mediaBase64?: string;
  /** Legacy / alternate casing from some responses */
  mediaFilename?: string;
  mediaContentType?: string;
  /** Engage API / wa-engage-v2 `ReferralMessage` — public URL for MMS preview */
  mediaUrl?: string;
  /** Same as mediaFilename; preferred when API returns camelCase */
  mediaFileName?: string;
  createdAt?: string;
  direction?: string; // 'inbound' or 'outbound'
  recordingUrl?: string;

  readBy?: string;
  readAt?: string;
}

export interface SendSmsPayload {
  referralId: string;
  patientId: string;
  body: string;
  senderName: string;
  mediaBase64?: string;
  mediaFilename?: string;
  mediaContentType?: string;
}

@Injectable({
  providedIn: 'root'
})
export class TwilioSmsService {
  get apiBaseUrl(): string {
    return environment.engageApiBaseUrl;
  }

  constructor(private readonly _dataService: DataService) {}

  getPatientMessages(patientId: string): Observable<TwilioMessage[]> {
    const url = `${this.apiBaseUrl}api/v1/twilio/messages/patient/${patientId}`;
    return this._dataService.get(url);
  }

  /** SMS thread for a lead before clinic patientId exists (symmetric to patient messages). */
  getReferralMessages(referralId: string): Observable<TwilioMessage[]> {
    const url = `${this.apiBaseUrl}api/v1/twilio/messages/referral/${encodeURIComponent(referralId)}`;
    return this._dataService.get(url);
  }

  /**
   * Mark the SMS thread as active (e.g. when opening the SMS composer).
   * POST `api/v1/twilio/messages/mark-active/{patientId}` — patient id when available.
   */
  markReferralMessagesActive(patientOrReferralId: string): Observable<unknown> {
    const id = encodeURIComponent(String(patientOrReferralId || "").trim());
    const url = `${this.apiBaseUrl}api/v1/twilio/messages/mark-active/${id}`;
    return this._dataService.post(url, {});
  }

  sendMessage(payload: SendSmsPayload): Observable<any> {
    const url = `${this.apiBaseUrl}api/v1/twilio/messages/send`;
    return this._dataService.post(url, payload);
  }

  /** Use when the referral is linked to a clinic patient (not a new lead-only thread). */
  checkPatientJoined(patientId: string): Observable<boolean> {
    const url = `${this.apiBaseUrl}api/v1/conversation-participants/check-patient/${patientId}`;
    return this._dataService.get(url).pipe(
      map((res: any) => (typeof res === 'boolean' ? res : res?.joined ?? res?.hasJoined ?? false))
    );
  }

  /**
   * New lead / no clinic patient id yet — use only when `check-patient` does not apply.
   * Engage API: many deployments expose POST `/check-referral` with `{ referralId }` (GET with
   * path param returns 404 on some Heroku builds). Try POST first, then GET path, then query.
   */
  checkReferralJoined(referralId: string): Observable<boolean> {
    const base = `${this.apiBaseUrl}api/v1/conversation-participants`;
    const id = encodeURIComponent(referralId);
    const url = `${this.apiBaseUrl}api/v1/conversation-participants/check/${id}`;
    return this._dataService.get(url).pipe(
      map((res: any) => (typeof res === 'boolean' ? res : res?.joined ?? res?.hasJoined ?? false))
    );
  }

  joinConversation(patientId: string): Observable<any> {
    const url = `${this.apiBaseUrl}api/v1/conversation-participants/join`;
    return this._dataService.post(url, { patientId });
  }

  joinConversationByReferral(referralId: string): Observable<any> {
    const url = `${this.apiBaseUrl}api/v1/conversation-participants/join`;
    return this._dataService.post(url, { referralId });
  }
}
